import React from 'react';
import styled from 'styled-components';

import {
  CARD_TYPES,
  WIZARD_CARDS_ELEVATION,
  WIZARD_COMPONENT,
} from '../../../shared/constants';

import * as generic from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { useWizardStore } from '../../../state/wizard/Wizard.store';
import { WizardItem } from '../../../types/wizard';
import AccordionItem from '../../molecules/accordion-item/AccordionItem';
import Media from '../../molecules/media/Media';
import WizardFieldset from '../../molecules/wizard-fieldset/WizardFieldset';
import { NavigationType } from '../../pages/wizard-page/WizardPage.types';

import WizardCheckbox from './WizardCheckbox';

interface WizardCardProps extends WizardItem {
  navigationType: NavigationType;
  cardElevation?: 'low' | 'high';
}

const WizardCard = ({
  id,
  value,
  title,
  items,
  visual,
  navigationType,
  cardElevation = 'low',
}: WizardCardProps) => {
  const { updateItem, nextStepIndex } = useWizardStore();
  const {
    body,
    videoUrl,
    accordionLabel,
    accordionBody,
    itemsAreGrouped,
    itemsLabel,
    itemsType,
    itemsHelperMessage,
    itemsOrientation,
  } = visual || {};
  const parentItemId = itemsAreGrouped ? id : undefined;

  const handleItemOnChange = (itemId: string, isChecked: boolean) => {
    updateItem(itemId, parentItemId, isChecked, '');

    if (navigationType === NavigationType.RESTRICTED) {
      nextStepIndex();
    }
  };

  const handleItemOnTextInput = (itemId: string, itemTextValue: string) => {
    updateItem(itemId, parentItemId, true, itemTextValue);
  };

  return (
    <CardItem cardElevation={cardElevation}>
      {title && <StyledTitle>{title}</StyledTitle>}
      {body && (
        <StyledBody>
          <generic.ReactMarkdownExtended componentType={CARD_TYPES.wizardCard}>
            {body.childMarkdownRemark.rawMarkdownBody}
          </generic.ReactMarkdownExtended>
        </StyledBody>
      )}
      {itemsAreGrouped ? (
        <WizardFieldset
          id={id}
          value={value}
          title={title as string}
          optionsLabel={itemsLabel}
          helperMessage={itemsHelperMessage}
          inputType={itemsType}
          layout={itemsOrientation}
          fields={items}
          required
          onValueChange={(_, item) => handleItemOnChange(item.id, !item.value)}
          customEventHandler={() => {}}
        />
      ) : (
        <StyledList>
          {items.map((item, index) => (
            <StyledItem key={index}>
              <WizardCheckbox
                {...item}
                isChecked={!!item.value}
                onChange={handleItemOnChange}
                onTextInput={handleItemOnTextInput}
              />
            </StyledItem>
          ))}
        </StyledList>
      )}
      {accordionLabel && accordionBody && (
        <AccordionItem
          title={accordionLabel}
          type={WIZARD_COMPONENT.wizardFaqItem}
        >
          <generic.ReactMarkdownExtended componentType={CARD_TYPES.wizardCard}>
            {accordionBody.childMarkdownRemark.rawMarkdownBody}
          </generic.ReactMarkdownExtended>
        </AccordionItem>
      )}
      {videoUrl && <Media src={videoUrl} type="Youtube-Embed" />}
    </CardItem>
  );
};

const CardItem = styled.li<{ cardElevation?: string }>`
  background-color: ${Colors.white};
  border-radius: 4px 4px 0px 0px;
  box-shadow: ${({ cardElevation, theme }) =>
    cardElevation === WIZARD_CARDS_ELEVATION.low
      ? theme.elevations.abnElevationWell
      : theme.elevations.abnPopover};
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: ${Spacings.L};
  `}
`;

const StyledList = styled.ul`
  list-style: none;
  margin: ${Spacings.M} 0 0;
  padding: 0;
`;

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
`;

const StyledTitle = styled(H3)`
  margin: 0 0 ${Spacings.XS} 0;
`;

const StyledBody = styled(Body)`
  color: ${Colors.abnNeutral400};
  margin-bottom: ${Spacings.M};
`;

export default WizardCard;
